import * as React from "react";

import { noop } from "../../../../../../Common/TypingUtils";
import { BindingPath } from "../../../../Engine/Core/CoreTypes";
import { nullElement } from "../../../TypingUtils";
import { ICandyComponentHandle } from "../../../../Engine/Core/ComponentHandleRegistry/ICandyComponentHandle";
import { ErrorLog, ErrorLogItem } from "../../../../Engine/Core/ErrorManager/Utils/Models";
import { useAsyncEffect } from "../../../../../../Common/UseAsyncEffect";
import { ErrorLogTooltipAttachmentState } from "../../../../Engine/Core/Store/FullFormState";

import { Root, TooltipContent } from "./ErrorsList.styles";
import { ErrorItemDescription } from "./ErrorItemDescription/ErrorItemDescription";
import { ErrorLogTooltip } from "./ErrorLogTooltip/ErrorLogTooltip";
import { ErrorsGroup } from "./ErrorsGroup/ErrorsGroup";
import { InterdocumentChecks } from "./ErrorsGroup/InterdocumentChecks";

export interface ErrorsListProps {
    errorLog: ErrorLog;
    showSuccesses: boolean;
    useExtendedErrorLog?: boolean;
    tooltipErrorId: undefined | string;
    tooltipBindingPath: BindingPath[] | undefined;
    onFindAndFocusControl: (
        attachmentId?: string
    ) => (
        paths: BindingPath[],
        highlanderPath?: string | null,
        errorItemId?: string
    ) => Promise<ICandyComponentHandle | undefined>;
    disableControlsTooltip: (bindingPaths: BindingPath[], errorId: string) => void;
    enableControlsTooltip: () => void;
    onBeforeNavigateToControl?: () => void;
    range?: [start: number, end: number];
    showJustification?: boolean;
    hideExternalChecks?: boolean;
    onNavigateToAttachment: (
        attachmentId: string,
        highlanderPath: string
    ) => Promise<ICandyComponentHandle | undefined>;
    clearAttachmentErrorLog: () => void;
    tooltipAttachment?: ErrorLogTooltipAttachmentState;
    enableControlsTooltipAttachment: () => void;
    additionalHeaderOffset?: number;
}

export function ErrorsList(props: ErrorsListProps): JSX.Element {
    const { tooltipBindingPath, tooltipErrorId, tooltipAttachment, errorLog } = props;
    const [anchorElement, setAnchorElement] = React.useState<undefined | HTMLElement>(undefined);
    React.useEffect(() => () => props.enableControlsTooltip(), []);

    React.useEffect(() => {
        if (errorLog.isChecking) {
            resetTarget();
        }
    }, [errorLog.isChecking]);

    useAsyncEffect(async () => {
        if (!tooltipAttachment) {
            return;
        }

        if (tooltipAttachment.attachmentId && tooltipAttachment.highlanderPath) {
            const target = await props.onNavigateToAttachment(
                tooltipAttachment.attachmentId,
                tooltipAttachment.highlanderPath
            );

            if (target) {
                setAnchorElement(target.getAnchorElement());
            } else {
                resetAttachmentTarget();
            }

            props.clearAttachmentErrorLog();
        }
    }, [
        tooltipAttachment?.highlanderPath,
        tooltipAttachment?.bindingPaths,
        tooltipAttachment?.errorId,
        tooltipAttachment?.attachmentId,
    ]);

    const handleFocusControlFromDescription = async (paths: BindingPath[], errorItem: ErrorLogItem) => {
        props.onBeforeNavigateToControl?.();
        const target = await props.onFindAndFocusControl()(paths, errorItem.highlanderPath, errorItem.id);
        if (target != undefined) {
            setAnchorElement(target.getAnchorElement());
            props.disableControlsTooltip(paths, errorItem.id);
        } else {
            resetTarget();
        }
    };

    const resetTarget = () => {
        props.enableControlsTooltip();
        setAnchorElement(undefined);
    };

    const resetAttachmentTarget = () => {
        props.enableControlsTooltipAttachment();
        setAnchorElement(undefined);
    };

    const handleFocusControlFromErrorItem = async (
        paths: string[],
        attachmentId?: string,
        highlanderPath?: string | null,
        errorItemId?: string
    ) => {
        const target = await props.onFindAndFocusControl(attachmentId)(paths, highlanderPath, errorItemId);
        return target;
    };

    const renderTooltipText = (): JSX.Element => {
        const activeTooltipErrorId = props.tooltipErrorId || props.tooltipAttachment?.errorId;
        const activeError =
            activeTooltipErrorId != undefined
                ? (props.errorLog.errors?.[activeTooltipErrorId] ??
                  props.errorLog.warnings?.[activeTooltipErrorId] ??
                  props.errorLog.successes?.[activeTooltipErrorId])
                : undefined;
        return (
            <TooltipContent>
                {activeError && (
                    <ErrorItemDescription
                        error={activeError}
                        onActivateItem={descriptionPaths =>
                            handleFocusControlFromDescription(descriptionPaths, activeError)
                        }
                        inTooltip={true}
                    />
                )}
            </TooltipContent>
        );
    };

    const renderTooltip = () => {
        const isAttachmentTooltip =
            tooltipAttachment?.bindingPaths && tooltipAttachment?.errorId && tooltipAttachment?.attachmentId;
        const isRegularTooltip = tooltipBindingPath && tooltipErrorId;

        if ((isAttachmentTooltip || isRegularTooltip) && anchorElement) {
            return (
                <ErrorLogTooltip
                    anchorElement={anchorElement}
                    render={renderTooltipText}
                    onCloseTooltip={() => {
                        resetTarget();
                        resetAttachmentTarget();
                    }}
                />
            );
        }

        return nullElement;
    };

    return (
        <Root>
            {errorLog.errorGroupInfos.map((group, index) =>
                props.hideExternalChecks && index > 0 ? null : (
                    <ErrorsGroup
                        tid={group.tid}
                        key={`${group.groupName}_${index}`}
                        group={group}
                        errors={errorLog.errors}
                        warnings={errorLog.warnings}
                        successes={errorLog.successes}
                        isMainGroup={index === 0}
                        showSuccesses={props.showSuccesses}
                        range={props.range}
                        showJustification={props.showJustification}
                        useExtendedErrorLog={props.useExtendedErrorLog}
                        onBeforeNavigateToControl={props.onBeforeNavigateToControl}
                        onFindAndFocusControl={(paths: string[], highlanderPath, errorItemId) =>
                            handleFocusControlFromErrorItem(paths, group.groupAttachmentId, highlanderPath, errorItemId)
                        }
                        disableControlsTooltip={props.disableControlsTooltip}
                        enableControlsTooltip={props.enableControlsTooltip}
                        setIsTargetEdited={noop}
                        setAnchorElement={setAnchorElement}
                        additionalHeaderOffset={props.additionalHeaderOffset}
                    />
                )
            )}
            {!props.hideExternalChecks && errorLog.interdocumentCheckerResult != undefined && (
                <InterdocumentChecks interdocumentCheckerResult={errorLog.interdocumentCheckerResult} />
            )}
            {renderTooltip()}
        </Root>
    );
}
